import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToBooking } from '../../services/tripService';
import { cancelBooking, modifyBooking } from '../../services/bookingService';
import { getFareConfig, getPeakSurcharge, getSystemConfig } from '../../services/fareService';
import { getRoutesWithScoring } from '../../services/routeService';
import { TripPinDisplay } from '../../components/booking/BookingComponents';
import { SOSButton } from '../../components/safety/SafetyComponents';
import ChatWindow from '../../components/messaging/ChatWindow';
import { triggerSOS } from '../../services/safetyService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function ActiveTrip() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [booking, setBooking] = useState(null);

  // Resolve a booking's scheduled pickup time to a JS Date (handles Firestore Timestamp shapes)
  const pickupTimeOf = (b) => {
    const t = b?.scheduledPickupTime;
    if (!t) return new Date(Date.now() + 3600000);
    if (t.toDate) return t.toDate();
    if (t._seconds) return new Date(t._seconds * 1000);
    return new Date(t);
  };

  // Authoritative route facts for any change: distance, duration, TOLLS (routeService) and
  // peak multiplier (fareService). Sent to modifyBooking so the server recomputes the fare.
  const computeRouteFacts = async ({ pickup, destination, stops, pickupTime, tier }) => {
    const { rates, peak } = await getFareConfig();
    const peakMultiplier = getPeakSurcharge(pickupTime, peak);
    const result = await getRoutesWithScoring({
      pickup, destination, pickupTime,
      tier: tier || 'go',
      fareRates: rates,
      peakMultiplier,
      stops: stops || [],
    });
    const rec = result.recommended;
    if (!rec) throw new Error('Could not compute the new route');
    return { estimatedDistance: rec.distanceKm, estimatedDuration: rec.durationMin, tolls: rec.totalToll || 0, peakMultiplier, fare: rec.fare ? rec.fare.totalFare : null };
  };
  const [showChat, setShowChat] = useState(false);
  const [minAdvMin, setMinAdvMin] = useState(60);
  const [fareCfg, setFareCfg] = useState(null);
  const [modifyPreview, setModifyPreview] = useState(null);
  useEffect(() => { getSystemConfig().then(c => setMinAdvMin(c.minAdvanceBookingMinutes ?? 60)).catch(() => {}); }, []);
  useEffect(() => { getFareConfig().then(setFareCfg).catch(() => {}); }, []);
  const [showCancel, setShowCancel] = useState(false);
  const [showModify, setShowModify] = useState(false);
  const [showAddStop, setShowAddStop] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [newStopAddress, setNewStopAddress] = useState('');
  const [newStop, setNewStop] = useState(null);
  const [addingStop, setAddingStop] = useState(false);
  const [modifyMode, setModifyMode] = useState(null); // null, 'datetime', 'route', 'passengers'
  const [modifyData, setModifyData] = useState({});
  const [modifying, setModifying] = useState(false);
  const [, forceTick] = useState(0);
  const [showFareBreakdown, setShowFareBreakdown] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    const unsub = subscribeToBooking(bookingId, setBooking);
    return unsub;
  }, [bookingId]);

  // Live countdown for pending_targeted offers
  useEffect(() => {
    if (booking?.status !== 'pending_targeted' || !booking.offerExpiresAt) return;
    const id = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [booking?.status, booking?.offerExpiresAt]);

  // Auto-navigate to rating page once trip completes (with 2.5s grace so user sees "completed" state)
  useEffect(() => {
    if (booking?.status === 'completed' && !booking.riderRated) {
      const t = setTimeout(() => navigate(`/rider/rate/${bookingId}`), 2500);
      return () => clearTimeout(t);
    }
  }, [booking?.status, booking?.riderRated, bookingId, navigate]);



  // Init Google Places autocomplete for add stop
  useEffect(() => {
    if (showAddStop && window.google) {
      setTimeout(() => {
        const input = document.getElementById('stop-input');
        if (!input) return;
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          componentRestrictions: { country: 'au' }, fields: ['geometry', 'formatted_address', 'place_id'],
        });
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            setNewStop({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), placeId: place.place_id });
            setNewStopAddress(place.formatted_address);
          }
        });
      }, 200);
    }
  }, [showAddStop]);

  // Init Google Places for route modification
  useEffect(() => {
    if (showModify && modifyMode === 'route' && window.google) {
      setTimeout(() => {
        const pickupInput = document.getElementById('modify-pickup-input');
        const destInput = document.getElementById('modify-dest-input');
        if (pickupInput) {
          const ac1 = new window.google.maps.places.Autocomplete(pickupInput, {
            componentRestrictions: { country: 'au' }, fields: ['geometry', 'formatted_address', 'place_id'],
          });
          ac1.addListener('place_changed', () => {
            const place = ac1.getPlace();
            if (place.geometry) {
              setModifyData(p => ({ ...p, newPickup: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), placeId: place.place_id }, newPickupAddress: place.formatted_address }));
            }
          });
        }
        if (destInput) {
          const ac2 = new window.google.maps.places.Autocomplete(destInput, {
            componentRestrictions: { country: 'au' }, fields: ['geometry', 'formatted_address', 'place_id'],
          });
          ac2.addListener('place_changed', () => {
            const place = ac2.getPlace();
            if (place.geometry) {
              setModifyData(p => ({ ...p, newDestination: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), placeId: place.place_id }, newDestinationAddress: place.formatted_address }));
            }
          });
        }
      }, 200);
    }
  }, [showModify, modifyMode]);

  // Live preview (distance, duration, tolls, fare) for route / date-time / add-stop changes.
  // Uses the same toll-aware computeRouteFacts as the actual submit, so the preview matches the charged price.
  useEffect(() => {
    if (!booking || !window.google) { setModifyPreview(null); return; }
    let pickup, dest, stops, pickupTime;
    if (showModify && modifyMode === 'route') {
      if (!modifyData.newPickup && !modifyData.newDestination) { setModifyPreview(null); return; }
      pickup = modifyData.newPickup || booking.pickup;
      dest = modifyData.newDestination || booking.destination;
      stops = booking.stops || [];
      pickupTime = pickupTimeOf(booking);
    } else if (showModify && modifyMode === 'datetime' && modifyData.date && modifyData.time) {
      pickup = booking.pickup; dest = booking.destination; stops = booking.stops || [];
      pickupTime = new Date(`${modifyData.date}T${modifyData.time}`);
    } else if (showAddStop && newStop) {
      pickup = booking.pickup; dest = booking.destination;
      stops = [...(booking.stops || []), { address: newStopAddress, location: newStop }];
      pickupTime = pickupTimeOf(booking);
    } else { setModifyPreview(null); return; }
    if (!(pickup?.lat && dest?.lat)) { setModifyPreview(null); return; }
    let cancelled = false;
    setModifyPreview({ loading: true });
    computeRouteFacts({ pickup, destination: dest, stops, pickupTime, tier: booking.tier })
      .then((f) => {
        if (cancelled) return;
        setModifyPreview({ loading: false, distance: f.estimatedDistance, duration: f.estimatedDuration, tolls: f.tolls, fare: f.fare });
        if (modifyMode === 'route') setModifyData((p) => ({ ...p, newDistance: f.estimatedDistance, newDuration: f.estimatedDuration }));
      })
      .catch(() => { if (!cancelled) setModifyPreview(null); });
    return () => { cancelled = true; };
  }, [showModify, modifyMode, showAddStop, newStop, newStopAddress, modifyData.newPickup, modifyData.newDestination, modifyData.date, modifyData.time, booking]);

  if (!booking) return <div className="p-10 text-center">Loading trip...</div>;

  const canCancel = ['pending_pool', 'pending_targeted', 'pending', 'confirmed', 'driverAssigned', 'en_route', 'arrived'].includes(booking.status);
  const canModify = ['pending_pool', 'pending_targeted', 'pending', 'confirmed', 'driverAssigned'].includes(booking.status);
  const canAddStop = ['pending_pool', 'pending_targeted', 'pending', 'confirmed', 'driverAssigned', 'en_route', 'arrived', 'in_progress'].includes(booking.status);

  const isActive = !['completed', 'cancelled'].includes(booking.status);
  const maxStops = 3;
  const currentStopCount = (booking.stops || []).length;

  // ═══════════════ CANCELLATION FEE LOGIC ═══════════════
  // Free if 24+ hours before pickup
  // 25% (min $10) if 2-24 hours before
  // 50% (min $20) if 30min - 2 hours before
  // 100% (min $30) if under 30 minutes before
  const CANCEL_DEFAULTS = [
    { minHoursBefore: 24, percent: 0, minFee: 0 },
    { minHoursBefore: 2, percent: 25, minFee: 10 },
    { minHoursBefore: 0.5, percent: 50, minFee: 20 },
    { minHoursBefore: 0, percent: 100, minFee: 30 },
  ];
  const cancelTiers = (Array.isArray(fareCfg?.rates?.cancellation?.tiers) && fareCfg.rates.cancellation.tiers.length ? fareCfg.rates.cancellation.tiers : CANCEL_DEFAULTS)
    .slice().sort((a, b) => b.minHoursBefore - a.minHoursBefore);

  const getCancelFee = () => {
    if (!booking.scheduledPickupTime || !booking.fareBreakdown) return 0;
    const pickupTime = booking.scheduledPickupTime.toDate ? booking.scheduledPickupTime.toDate() : new Date(booking.scheduledPickupTime);
    const hoursUntilPickup = (pickupTime - Date.now()) / 3600000;
    const fareExclTolls = (booking.fareBreakdown.totalFare || 0) - (booking.fareBreakdown.tolls || 0);
    const tier = cancelTiers.find((t) => hoursUntilPickup > t.minHoursBefore) || cancelTiers[cancelTiers.length - 1];
    return (tier.percent || 0) <= 0 ? 0 : Math.max(tier.minFee || 0, Math.round(fareExclTolls * (tier.percent / 100) * 100) / 100);
  };

  const getCancelDescription = () => {
    if (!booking.scheduledPickupTime) return '';
    const pickupTime = booking.scheduledPickupTime.toDate ? booking.scheduledPickupTime.toDate() : new Date(booking.scheduledPickupTime);
    const hours = (pickupTime - Date.now()) / 3600000;
    const tier = cancelTiers.find((t) => hours > t.minHoursBefore) || cancelTiers[cancelTiers.length - 1];
    if ((tier.percent || 0) <= 0) return '';
    return `${tier.percent}% cancellation fee (minimum ${(tier.minFee || 0).toFixed(2)}) applies if you cancel now.`;
  };

  // ═══════════════ STOP FEE ═══════════════
  const STOP_FEE = (fareCfg?.rates?.perStopFee != null) ? fareCfg.rates.perStopFee : 5.00; // configurable via Fare Config

  const handleCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Please select a reason', { id: 'cancel-reason' }); return; }
    setCancelling(true);
    try {
      await cancelBooking(bookingId, cancelReason);
      toast.success('Booking cancelled');
      setShowCancel(false);
      navigate('/rider');
    } catch (err) {
      toast.error(err.message || 'Failed to cancel');
    }
    setCancelling(false);
  };

  const handleRemoveStop = async (index) => {
    if (!window.confirm('Remove this stop? The fare will be updated.')) return;
    try {
      const updatedStops = (booking.stops || []).filter((_, i) => i !== index);
      const facts = await computeRouteFacts({
        pickup: booking.pickup, destination: booking.destination,
        stops: updatedStops, pickupTime: pickupTimeOf(booking), tier: booking.tier,
      });
      await modifyBooking(bookingId, {
        stops: updatedStops,
        estimatedDistance: facts.estimatedDistance,
        estimatedDuration: facts.estimatedDuration,
        tolls: facts.tolls,
        peakMultiplier: facts.peakMultiplier,
      });
      toast.success('Stop removed — fare updated');
    } catch (err) {
      toast.error(err.message || 'Failed to remove stop');
    }
  };

  const handleAddStop = async () => {
    if (!newStop) { toast.error('Select a stop address', { id: 'stop-required' }); return; }
    if (currentStopCount >= maxStops) { toast.error(`Maximum ${maxStops} stops allowed`, { id: 'max-stops' }); return; }
    setAddingStop(true);
    try {
      const updatedStops = [...(booking.stops || []), {
        address: newStopAddress,
        location: newStop,
        addedAt: new Date().toISOString(),
        stopFee: STOP_FEE,
        waitingFreeMinutes: 5,
      }];
      const facts = await computeRouteFacts({
        pickup: booking.pickup, destination: booking.destination,
        stops: updatedStops, pickupTime: pickupTimeOf(booking), tier: booking.tier,
      });
      await modifyBooking(bookingId, {
        stops: updatedStops,
        estimatedDistance: facts.estimatedDistance,
        estimatedDuration: facts.estimatedDuration,
        tolls: facts.tolls,
        peakMultiplier: facts.peakMultiplier,
      });
      toast.success('Stop added — fare updated');
      setShowAddStop(false);
      setNewStopAddress('');
      setNewStop(null);
    } catch (err) {
      toast.error(err.message || 'Failed to add stop');
    }
    setAddingStop(false);
  };

  const handleModifySubmit = async (type) => {
    setModifying(true);
    try {
      const changes = {};
      if (type === 'datetime') {
        if (!modifyData.date || !modifyData.time) { toast.error('Select date and time', { id: 'modify-dt' }); setModifying(false); return; }
        const newPickup = new Date(`${modifyData.date}T${modifyData.time}`);
        if ((newPickup - Date.now()) / 60000 < minAdvMin) { toast.error(minAdvMin <= 0 ? 'Pickup time cannot be in the past' : 'Pickup time is too soon', { id: 'modify-min' }); setModifying(false); return; }
        changes.scheduledPickupTime = newPickup.toISOString();
        const facts = await computeRouteFacts({
          pickup: booking.pickup, destination: booking.destination,
          stops: booking.stops || [], pickupTime: newPickup, tier: booking.tier,
        });
        changes.estimatedDistance = facts.estimatedDistance;
        changes.estimatedDuration = facts.estimatedDuration;
        changes.tolls = facts.tolls;
        changes.peakMultiplier = facts.peakMultiplier;
      }
      if (type === 'route') {
        if (modifyData.newPickup) { changes.pickup = modifyData.newPickup; changes.pickupAddress = modifyData.newPickupAddress; }
        if (modifyData.newDestination) { changes.destination = modifyData.newDestination; changes.destinationAddress = modifyData.newDestinationAddress; }
        if (!modifyData.newPickup && !modifyData.newDestination) { toast.error('Change at least one address', { id: 'modify-addr' }); setModifying(false); return; }
        const facts = await computeRouteFacts({
          pickup: modifyData.newPickup || booking.pickup,
          destination: modifyData.newDestination || booking.destination,
          stops: booking.stops || [], pickupTime: pickupTimeOf(booking), tier: booking.tier,
        });
        changes.estimatedDistance = facts.estimatedDistance;
        changes.estimatedDuration = facts.estimatedDuration;
        changes.tolls = facts.tolls;
        changes.peakMultiplier = facts.peakMultiplier;
      }
      if (type === 'passengers') {
        const pax = modifyData.passengers || booking.passengerCount;
        const maxPax = { go: 4, comfy: 4, plus: 6, vip: 4, vipPlus: 6 }[booking.tier] || 4;
        changes.passengerCount = pax;
        changes.luggage = { small: modifyData.smallBags ?? booking.luggage?.small ?? 0, large: modifyData.largeBags ?? booking.luggage?.large ?? 0 };
        if (pax > maxPax) {
          if (booking.tier === 'vip') changes.tier = 'vipPlus';
          else if (pax > 4) changes.tier = 'plus';
        }
      }
      await modifyBooking(bookingId, changes);
      toast.success('Booking updated');
      setModifyMode(null);
      setModifyData({});
      setShowModify(false);
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
    setModifying(false);
  };

  const statusLabels = {
    pending_pool: 'Finding your driver...',
    pending_targeted: 'Waiting for your preferred driver...',
    pending: 'Finding your driver...',
    confirmed: 'Driver confirmed!',
    driverAssigned: 'Driver assigned!',
    en_route: 'Driver is on the way',
    arrived: 'Driver has arrived',
    pin_verified: 'PIN verified — trip starting',
    in_progress: 'Trip in progress',
    completed: 'Trip completed',
    cancelled: 'Trip cancelled',
  };
  const statusColors = {
    pending_pool: '#fbbf24', pending_targeted: '#a855f7', pending: '#fbbf24',
    confirmed: '#6366f1', driverAssigned: '#6366f1', en_route: '#3b82f6',
    arrived: '#2dd4a8', pin_verified: '#2dd4a8', in_progress: '#2dd4a8',
    completed: '#5a6578', cancelled: '#ff6b6b',
  };

  const offerSecondsRemaining = (() => {

    if (booking?.status !== 'pending_targeted' || !booking.offerExpiresAt) return null;
    const expiresAt = booking.offerExpiresAt.toDate ? booking.offerExpiresAt.toDate() : new Date(booking.offerExpiresAt);
    return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  })();

  const formatCountdown = (seconds) => {
    if (seconds == null) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };


  const handleSOS = async () => {
    try {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        await triggerSOS(bookingId, userProfile.id, 'rider', { lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success('SOS activated — admin has been alerted');
      });
    } catch (err) { toast.error('Failed to activate SOS'); }
  };

  const cancelFee = getCancelFee();

  return (
    <div className="max-w-[600px] mx-auto p-6">
      {/* Navigation */}
      <div className="flex justify-between items-center mb-5">
        <button onClick={() => navigate('/rider')} className="bg-transparent border-0 cursor-pointer text-brand-primary font-semibold text-[0.9rem]">← Home</button>
        <button onClick={() => navigate('/rider/book')} className="px-4 py-2 rounded-full border-[1.5px] border-brand-primary bg-white cursor-pointer font-semibold text-[0.85rem] text-brand-primary">+ New Booking</button>
      </div>

      {/* Status Header */}
      <div className="text-center p-6 rounded-[20px] bg-white border border-slate-200 mb-5">
        <div className="w-3 h-3 rounded-full mx-auto mb-3" style={{ background: statusColors[booking.status], boxShadow: `0 0 12px ${statusColors[booking.status]}50` }} />
        <h2 className="font-serif mt-0 mb-1 text-brand-dark">{statusLabels[booking.status]}</h2>
        <div className="text-[0.85rem] text-muted flex items-center gap-2">
          <span>Ref: {booking.referenceNumber}</span>
          {booking.tier && <span className="px-2 py-0.5 rounded-full text-[0.72rem] font-bold" style={{ background: '#0a1628', color: '#fff' }}>{({ go: 'Go', comfy: 'Comfy', plus: 'Plus', vip: 'VIP', vipPlus: 'VIP+' }[booking.tier] || booking.tier)}</span>}
        </div>
        {booking.status === 'pending_pool' && (
          <div className="mt-3 p-2.5 rounded-[10px] text-[0.85rem]" style={{ background: '#fffbeb', color: '#92400e' }}>
            Visible to <strong>{booking.qualifiedDriverCount ?? '…'} qualified driver{booking.qualifiedDriverCount === 1 ? '' : 's'}</strong> in your area
          </div>
        )}
        {booking.status === 'pending_targeted' && offerSecondsRemaining != null && (
          <div className="mt-3 p-2.5 rounded-[10px] text-[0.85rem]" style={{ background: '#faf5ff', color: '#6b21a8' }}>
            {offerSecondsRemaining > 0 ? (
              <>Driver has <strong>{formatCountdown(offerSecondsRemaining)}</strong> to respond. If they don't, your booking opens to all drivers.</>
            ) : (
              <>Offer expired — moving to all drivers...</>
            )}
          </div>
        )}
      </div>


      {/* Trip PIN — visible when driver has arrived */}
      {['arrived'].includes(booking.status) && booking.tripPin && (
        <div className="mb-5"><TripPinDisplay pin={booking.tripPin} /></div>
      )}

      {/* Route Details with stops */}
      <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5">
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center gap-0.5">
            <div className="w-3 h-3 rounded-full bg-brand-primary" />
            {(booking.stops || []).map((_, i) => (
              <React.Fragment key={i}>
                <div className="w-0.5 h-5 bg-slate-200" />
                <div className="w-2.5 h-2.5 rounded-full border-2 border-white" style={{ background: '#fbbf24' }} />
              </React.Fragment>
            ))}
            <div className="w-0.5 h-5 bg-slate-200" />
            <div className="w-3 h-3 rounded-full" style={{ background: '#ff6b6b' }} />
          </div>
          <div className="flex-1">
            <div className="mb-3">
              <div className="text-[0.75rem] text-muted uppercase">Pickup</div>
              <div className="font-semibold text-brand-dark">{booking.pickupAddress}</div>
            </div>
            {(booking.stops || []).map((stop, i) => (
              <div key={i} className="mb-3">
                <div className="text-[0.75rem] uppercase font-semibold flex items-center gap-2" style={{ color: '#fbbf24' }}>
                  <span>Stop {i + 1} <span className="text-muted font-normal">(+${(stop.stopFee || STOP_FEE).toFixed(2)} fee · 5 min free wait)</span></span>
                  {canAddStop && (
                    <button onClick={() => handleRemoveStop(i)} className="bg-transparent border-0 cursor-pointer p-0 text-[0.7rem] font-semibold" style={{ color: '#ff6b6b' }}>Remove</button>
                  )}
                </div>
                <div className="font-semibold text-brand-dark">{stop.address}</div>
              </div>
            ))}
            <div>
              <div className="text-[0.75rem] text-muted uppercase">Destination</div>
              <div className="font-semibold text-brand-dark">{booking.destinationAddress}</div>
            </div>
          </div>
        </div>

        {/* Scheduled time */}
        {booking.scheduledPickupTime && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-[0.85rem] text-body">
            Scheduled: {(booking.scheduledPickupTime.toDate ? booking.scheduledPickupTime.toDate() : new Date(booking.scheduledPickupTime)).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* Action Buttons — Cancel, Modify, Add Stop */}
      {isActive && (
        <div className="flex gap-2 mb-5 flex-wrap">
          {canAddStop && currentStopCount < maxStops && (
            <button onClick={() => { setShowAddStop(!showAddStop); setShowCancel(false); setShowModify(false); }}
              className="flex-1 px-3.5 py-2.5 rounded-xl border-[1.5px] bg-white cursor-pointer font-semibold text-[0.85rem] text-center"
              style={{ borderColor: '#3b82f6', color: '#3b82f6' }}>
              + Add Stop
            </button>
          )}
          {canModify && (
            <button onClick={() => { setShowModify(!showModify); setModifyMode(null); setModifyData({}); setShowCancel(false); setShowAddStop(false); }}
              className="flex-1 px-3.5 py-2.5 rounded-xl border-[1.5px] bg-white cursor-pointer font-semibold text-[0.85rem] text-center"
              style={{ borderColor: '#fbbf24', color: '#b45309' }}>

              ✏️ Modify
            </button>
          )}
          {canCancel && (
            <button onClick={() => { setShowCancel(!showCancel); setShowModify(false); setShowAddStop(false); }}
              className="flex-1 px-3.5 py-2.5 rounded-xl border-[1.5px] bg-white cursor-pointer font-semibold text-[0.85rem] text-center"
              style={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}>
              ✕ Cancel Trip
            </button>
          )}
        </div>
      )}

      {/* Add Stop Panel */}
      {showAddStop && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <h3 className="font-serif text-[1.2rem] text-brand-dark mt-0 mb-2">Add a Stop</h3>
          <p className="text-[0.85rem] text-body mb-1">
            Add an intermediate stop to your trip. Fare will be recalculated.
          </p>
          <div className="p-2.5 rounded-lg mb-3" style={{ background: '#eff6ff' }}>
            <div className="text-[0.8rem] leading-normal" style={{ color: '#1e40af' }}>
              <strong>Stop fee:</strong> ${STOP_FEE.toFixed(2)} per stop + extra distance/time charges<br />
              <strong>Free waiting:</strong> 5 minutes at each stop, then $1.10/min<br />
              <strong>Stops remaining:</strong> {maxStops - currentStopCount} of {maxStops}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Stop address *</label>
            <input id="stop-input" className="form-input" placeholder="Search for address..." defaultValue={newStopAddress} />
          </div>
          {newStopAddress && (
            <div className="p-2.5 rounded-lg text-[0.85rem] mb-3" style={{ background: '#f0fdf4', color: '#059669' }}>
              ✓ {newStopAddress}
            </div>
          )}
          {modifyPreview?.loading && <div className="p-2.5 rounded-lg text-[0.85rem] mb-3 text-muted" style={{ background: '#f8fafc' }}>Calculating new fare…</div>}
          {modifyPreview && !modifyPreview.loading && modifyPreview.fare != null && (
            <div className="p-2.5 rounded-lg text-[0.85rem] mb-3" style={{ background: '#f0fdf4', color: '#059669' }}>
              New estimated fare <strong>${modifyPreview.fare.toFixed(2)}</strong> · {modifyPreview.distance}km{modifyPreview.tolls > 0 && <> · tolls ${modifyPreview.tolls.toFixed(2)}</>}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => { setShowAddStop(false); setNewStopAddress(''); setNewStop(null); }} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.9rem]">Cancel</button>
            <button onClick={handleAddStop} disabled={addingStop || !newStop} className="flex-1 px-5 py-3 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.9rem]">
              {addingStop ? 'Adding...' : `Add Stop (+$${STOP_FEE.toFixed(2)})`}
            </button>
          </div>
        </div>
      )}

{/* Modify Panel */}
      {showModify && !modifyMode && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <h3 className="font-serif text-[1.2rem] text-brand-dark mt-0 mb-2">Modify Booking</h3>
          <p className="text-[0.85rem] text-body mb-4">
            What would you like to change? Only affected settings will be updated.
          </p>
          <div className="flex flex-col gap-2">
            <button onClick={() => setModifyMode('datetime')} className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 bg-white cursor-pointer text-left w-full">
              <span className="text-[1.1rem]">📅</span>
              <div>
                <div className="font-semibold">Change date/time</div>
                <div className="text-[0.8rem] text-muted">May affect peak pricing</div>
              </div>
            </button>
            <button onClick={() => setModifyMode('route')} className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 bg-white cursor-pointer text-left w-full">
              <span className="text-[1.1rem]">📍</span>
              <div>
                <div className="font-semibold">Change pickup or destination</div>
                <div className="text-[0.8rem] text-muted">Recalculates distance, time & tolls</div>
              </div>
            </button>
            <button onClick={() => setModifyMode('passengers')} className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 bg-white cursor-pointer text-left w-full">
              <span className="text-[1.1rem]">👥</span>
              <div>
                <div className="font-semibold">Change passengers or luggage</div>
                <div className="text-[0.8rem] text-muted">May require a different vehicle tier</div>
              </div>
            </button>
          </div>
          <button onClick={() => setShowModify(false)} className="px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.9rem] mt-3 w-full">Close</button>
        </div>
      )}

      {/* Modify: Date/Time */}
      {showModify && modifyMode === 'datetime' && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <h3 className="font-serif text-[1.2rem] text-brand-dark mt-0 mb-2">Change Date & Time</h3>
          <div className="form-group">
            <label className="form-label">New date *</label>
            <input type="date" className="form-input" value={modifyData.date || ''} onChange={(e) => setModifyData(p => ({ ...p, date: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
          </div>
          <div className="form-group">
            <label className="form-label">New time *</label>
            <input type="time" className="form-input" value={modifyData.time || ''} onChange={(e) => setModifyData(p => ({ ...p, time: e.target.value }))} />
          </div>
          {modifyData.date && modifyData.time && (() => {
            const newPickup = new Date(`${modifyData.date}T${modifyData.time}`);
            const minsAhead = (newPickup - Date.now()) / 60000;
            const lead = minAdvMin <= 0 ? 'now' : (minAdvMin % 60 === 0 ? `${minAdvMin / 60} hour${minAdvMin / 60 > 1 ? 's' : ''}` : `${minAdvMin} minutes`);
            if (minsAhead < minAdvMin) return <div className="text-[0.85rem] mb-3" style={{ color: '#ff6b6b' }}>{minAdvMin <= 0 ? 'Pickup time cannot be in the past' : `Must be at least ${lead} from now`}</div>;
            return (
              <div className="p-2.5 rounded-lg text-[0.85rem] mb-3" style={{ background: '#f0fdf4', color: '#059669' }}>
                ✓ Valid pickup time
                {modifyPreview?.loading && <div className="mt-1 text-muted">Updating fare…</div>}
                {modifyPreview && !modifyPreview.loading && modifyPreview.fare != null && (
                  <div className="mt-1 text-brand-dark">New estimated fare <strong>${modifyPreview.fare.toFixed(2)}</strong>{modifyPreview.tolls > 0 && <> · tolls ${modifyPreview.tolls.toFixed(2)}</>}</div>
                )}
              </div>
            );
          })()}
          <div className="flex gap-2">
            <button onClick={() => setModifyMode(null)} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.9rem]">Back</button>
            <button onClick={() => handleModifySubmit('datetime')} disabled={modifying} className="flex-1 px-5 py-3 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.9rem]">
              {modifying ? 'Updating...' : 'Update Date/Time'}
            </button>
          </div>
        </div>
      )}

      {/* Modify: Route */}
      {showModify && modifyMode === 'route' && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <h3 className="font-serif text-[1.2rem] text-brand-dark mt-0 mb-2">Change Route</h3>
          <div className="form-group">
            <label className="form-label">Pickup address</label>
            <input id="modify-pickup-input" className="form-input" placeholder="Search new pickup..." defaultValue={booking.pickupAddress} />
          </div>
          <div className="form-group">
            <label className="form-label">Destination</label>
            <input id="modify-dest-input" className="form-input" placeholder="Search new destination..." defaultValue={booking.destinationAddress} />
          </div>
          {(modifyData.newDistance > 0 || modifyPreview) && (
            <div className="p-2.5 rounded-lg text-[0.85rem] mb-3" style={{ background: '#f0fdf4', color: '#059669' }}>
              {modifyData.newDistance > 0 && (
                <span>
                  New route: {modifyData.newDistance}km · ~{modifyData.newDuration} min
                  {modifyData.newDistance !== booking.estimatedDistance && (
                    <span style={{ color: modifyData.newDistance > booking.estimatedDistance ? '#ff6b6b' : '#059669' }}>
                      {' '}({modifyData.newDistance > booking.estimatedDistance ? '+' : ''}{(modifyData.newDistance - booking.estimatedDistance).toFixed(1)}km)
                    </span>
                  )}
                </span>
              )}
              {modifyPreview?.loading && <div className="mt-1 text-muted">Calculating tolls & fare…</div>}
              {modifyPreview && !modifyPreview.loading && (
                <div className="mt-1 text-brand-dark">
                  Tolls ${(modifyPreview.tolls || 0).toFixed(2)}
                  {modifyPreview.fare != null && <> · New estimated fare <strong>${modifyPreview.fare.toFixed(2)}</strong></>}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => setModifyMode(null)} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.9rem]">Back</button>
            <button onClick={() => handleModifySubmit('route')} disabled={modifying} className="flex-1 px-5 py-3 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.9rem]">
              {modifying ? 'Updating...' : 'Update Route'}
            </button>
          </div>
        </div>
      )}

      {/* Modify: Passengers */}
      {showModify && modifyMode === 'passengers' && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <h3 className="font-serif text-[1.2rem] text-brand-dark mt-0 mb-2">Change Passengers & Luggage</h3>
          <div className="form-group">
            <label className="form-label">Passengers</label>
            <div className="flex items-center gap-4">
              <button onClick={() => setModifyData(p => ({ ...p, passengers: Math.max(1, (p.passengers || booking.passengerCount) - 1) }))}
                className="w-10 h-10 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer text-[1.1rem] flex items-center justify-center">−</button>
              <span className="text-[1.3rem] font-bold min-w-[30px] text-center">{modifyData.passengers || booking.passengerCount}</span>
              <button onClick={() => setModifyData(p => ({ ...p, passengers: Math.min(8, (p.passengers || booking.passengerCount) + 1) }))}
                className="w-10 h-10 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer text-[1.1rem] flex items-center justify-center">+</button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Small bags</label>
            <div className="flex items-center gap-4">
              <button onClick={() => setModifyData(p => ({ ...p, smallBags: Math.max(0, (p.smallBags ?? booking.luggage?.small ?? 0) - 1) }))}
                className="w-10 h-10 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer text-[1.1rem] flex items-center justify-center">−</button>
              <span className="font-semibold min-w-[20px] text-center">{modifyData.smallBags ?? booking.luggage?.small ?? 0}</span>
              <button onClick={() => setModifyData(p => ({ ...p, smallBags: (p.smallBags ?? booking.luggage?.small ?? 0) + 1 }))}
                className="w-10 h-10 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer text-[1.1rem] flex items-center justify-center">+</button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Large bags</label>
            <div className="flex items-center gap-4">
              <button onClick={() => setModifyData(p => ({ ...p, largeBags: Math.max(0, (p.largeBags ?? booking.luggage?.large ?? 0) - 1) }))}
                className="w-10 h-10 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer text-[1.1rem] flex items-center justify-center">−</button>
              <span className="font-semibold min-w-[20px] text-center">{modifyData.largeBags ?? booking.luggage?.large ?? 0}</span>
              <button onClick={() => setModifyData(p => ({ ...p, largeBags: (p.largeBags ?? booking.luggage?.large ?? 0) + 1 }))}
                className="w-10 h-10 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer text-[1.1rem] flex items-center justify-center">+</button>
            </div>
          </div>
          {/* Tier warning */}
          {(() => {
            const pax = modifyData.passengers || booking.passengerCount;
            const maxPax = { go: 4, comfy: 4, plus: 6, vip: 4, vipPlus: 6 }[booking.tier] || 4;
            if (pax > maxPax) {
              const suggestedTier = pax > 4 ? (booking.tier === 'vip' ? 'vipPlus' : 'plus') : booking.tier;
              return (
                <div className="p-2.5 rounded-lg mb-3" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <div className="text-[0.85rem] font-semibold" style={{ color: '#dc2626' }}>
                    {pax} passengers won't fit in {booking.tier} (max {maxPax}). Tier will be upgraded to {suggestedTier}.
                  </div>
                </div>
              );
            }
            return null;
          })()}
          <div className="flex gap-2">
            <button onClick={() => setModifyMode(null)} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.9rem]">Back</button>
            <button onClick={() => handleModifySubmit('passengers')} disabled={modifying} className="flex-1 px-5 py-3 rounded-xl border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.9rem]">
              {modifying ? 'Updating...' : 'Update Passengers'}
            </button>
          </div>
        </div>
      )}


      {/* Cancel Panel */}
      {showCancel && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          <h3 className="font-serif text-[1.2rem] mt-0 mb-2" style={{ color: '#ff6b6b' }}>Cancel Booking</h3>
          {cancelFee > 0 ? (
            <div className="p-3 rounded-[10px] mb-4" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
              <div className="font-semibold mb-1" style={{ color: '#dc2626' }}>Cancellation fee: ${cancelFee.toFixed(2)}</div>
              <div className="text-[0.8rem] text-body">{getCancelDescription()}</div>
            </div>
          ) : (
            <div className="p-3 rounded-[10px] mb-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div className="font-semibold" style={{ color: '#059669' }}>Free cancellation</div>
              <div className="text-[0.8rem] text-body">More than 24 hours before pickup — no fee.</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Reason for cancellation *</label>
            <select className="form-input form-select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
              <option value="">Select a reason...</option>
              <option value="change_of_plans">Change of plans</option>
              <option value="found_alternative">Found alternative transport</option>
              <option value="wrong_details">Entered wrong details</option>
              <option value="driver_taking_long">Driver taking too long</option>
              <option value="emergency">Emergency</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setShowCancel(false); setCancelReason(''); }} className="flex-1 px-5 py-3 rounded-xl border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.9rem]">Keep Booking</button>
            <button onClick={handleCancel} disabled={cancelling} className="flex-1 px-5 py-3 rounded-xl border-0 text-white cursor-pointer font-bold text-[0.9rem]" style={{ background: '#ff6b6b' }}>
              {cancelling ? 'Cancelling...' : `Cancel Trip${cancelFee > 0 ? ` ($${cancelFee.toFixed(2)})` : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Driver Info — masked name + vehicle details for pickup identification */}
      {booking.driverId && booking.driverName && (
        <div className="p-5 rounded-2xl bg-white border border-slate-200 mb-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center font-bold text-[1.2rem]">
            {booking.driverName?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-brand-dark">{booking.driverName}</div>
            {(booking.driverVehicleColour || booking.driverVehicleMake || booking.driverVehicleModel) && (
              <div className="text-[0.85rem] text-body">
                {[booking.driverVehicleColour, booking.driverVehicleMake, booking.driverVehicleModel].filter(Boolean).join(' ')}
              </div>
            )}
            {booking.driverPlate && (
              <div className="inline-block mt-1.5 px-2.5 py-[3px] rounded-md text-[0.8rem] font-bold" style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#713f12', letterSpacing: '0.05em' }}>
                {booking.driverPlate}
              </div>
            )}
          </div>
          <button onClick={() => setShowChat(!showChat)}
            className="px-4 py-2.5 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.85rem]">{showChat ? 'Hide' : 'Chat'}</button>
        </div>
      )}

      {/* Chat */}
      {showChat && booking.driverId && (
        <div className="mb-5 rounded-2xl border border-slate-200 overflow-hidden">
          <ChatWindow bookingId={bookingId} userId={userProfile.id} userRole="rider" otherPartyName={booking.driverName || 'Driver'} booking={booking} />
        </div>
      )}


      {/* Fare breakdown — total is the authoritative fareBreakdown.totalFare (already includes
          stop fees, CTP, booking fee, levy and GST). Expandable so the rider can see line items at any time. */}
      {booking.fareBreakdown && (() => {
        const fb = booking.fareBreakdown;
        const isFinal = booking.status === 'completed';
        const rows = [
          ['Base fare', fb.baseFare],
          ['Distance', fb.distanceFare],
          ['Duration', fb.durationFare],
          [`Peak surcharge${fb.peakPercent ? ` (${fb.peakPercent}%)` : ''}`, fb.peakSurcharge],
          ['Tolls', fb.tolls],
          [`Stop fee${(fb.stopsCount || 0) > 1 ? 's' : ''}${fb.stopsCount ? ` (${fb.stopsCount})` : ''}`, fb.stopFeeTotal],
          ['Stop wait charges', fb.dwellCharges],
          ['NSW CTP levy', fb.ctp],
          ['Booking fee', fb.bookingFee],
          ['Passenger levy', fb.levy],
          ['GST (10%)', fb.gst],
        ].filter(([, v]) => typeof v === 'number' && v > 0);
        return (
          <div className="p-5 rounded-2xl bg-slate-50 mb-5">
            <button
              onClick={() => setShowFareBreakdown((v) => !v)}
              className="w-full flex justify-between items-center bg-transparent border-0 p-0 cursor-pointer"
            >
              <span className="text-body font-semibold">{isFinal ? 'Final fare' : 'Estimated fare'}</span>
              <span className="flex items-center gap-2">
                <span className="font-extrabold text-[1.3rem] text-brand-dark">${(fb.totalFare || 0).toFixed(2)}</span>
                <span className="text-muted text-[0.7rem]">{showFareBreakdown ? '▲' : '▼'}</span>
              </span>
            </button>

            {booking.surchargeApplied && (
              <div className="mt-1 text-[0.8rem]" style={{ color: '#92400e' }}>
                Includes an approved route-deviation surcharge of ${(booking.surchargeAmount || 0).toFixed(2)} ({booking.actualDistanceKm} km actual vs {booking.estimatedDistanceKm} km quoted).
              </div>
            )}

            {!showFareBreakdown && fb.tolls > 0 && (
              <div className="flex justify-between mt-1 text-[0.85rem]">
                <span className="text-muted">Includes tolls</span>
                <span className="text-body">${fb.tolls.toFixed(2)}</span>
              </div>
            )}

            {showFareBreakdown && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                {rows.map(([label, value]) => (
                  <div key={label} className="flex justify-between mt-1 text-[0.85rem]">
                    <span className="text-muted">{label}</span>
                    <span className="text-body">${value.toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-2 pt-2 border-t border-slate-200 font-bold text-brand-dark">
                  <span>Total</span>
                  <span>${(fb.totalFare || 0).toFixed(2)}</span>
                </div>
                <div className="mt-2 text-[0.72rem] text-muted leading-normal">
                  Fare includes the $1.20 NSW Passenger Service Levy and CTP.{isFinal ? '' : ' Final fare may vary if the route, stops or time change.'}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Cancellation Policy Info */}
      {isActive && (
        <div className="p-3.5 rounded-xl bg-slate-50 mb-5">
          <div className="text-[0.8rem] text-muted leading-normal">
            <strong className="text-body">Cancellation policy:</strong>{' '}
            {cancelTiers.map((t, i) => (
              <span key={i}>{i > 0 ? ' · ' : ''}{(t.percent || 0) === 0 ? 'Free' : `${t.percent}% (min ${(t.minFee || 0).toFixed(0)})`} if &gt;{t.minHoursBefore}h before</span>
            ))}
          </div>
          <div className="text-[0.8rem] text-muted leading-normal mt-1">
            <strong className="text-body">Stops:</strong> Max {maxStops} stops · ${STOP_FEE.toFixed(2)} per stop · 5 min free wait, then $1.10/min.
          </div>
        </div>
      )}

      {/* SOS Button — visible during active trip statuses */}
      {['en_route', 'arrived', 'pin_verified', 'in_progress'].includes(booking.status) && (
        <SOSButton onActivate={handleSOS} />
      )}
    <BackButtonBottom to="/rider" label="Home" />
    </div>
  );
}